
import { Message } from './types';
import { discovery } from './DiscoveryService';

// Logic: Browser RAM is the hard limit for WebRTC accumulation.
// Most mobile browsers crash above 500MB, desktop above 1.5GB.
const MAX_BUFFERED_SIZE = 1024 * 1024 * 1024; // 1GB Memory Guard
const CHUNK_SIZE = 16384; 
const BUFFER_THRESHOLD = 65536; 
const HEARTBEAT_INTERVAL = 10000; 

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    // STUN servers for NAT traversal
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    // Fix #4: TURN relay servers for symmetric NAT (corporate/mobile networks).
    // Without TURN, peers behind symmetric NAT (VPN, campus WiFi, mobile data)
    // can never establish a connection — ICE fails silently.
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turns:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ],
  iceCandidatePoolSize: 10
};

class WebRTCService {
  private connections: Map<string, RTCPeerConnection> = new Map();
  private dataChannels: Map<string, RTCDataChannel> = new Map();
  private receiveBuffers: Map<string, { chunks: ArrayBuffer[], receivedSize: number, meta: any }> = new Map();
  private heartbeats: Map<string, any> = new Map();
  private queuedCandidates: Map<string, RTCIceCandidateInit[]> = new Map();

  private onMessageReceived?: (msg: Message) => void;
  private onProgressUpdate?: (messageId: string, peerId: string, progress: number) => void;
  private onPairRequestReceived?: (fromId: string, fromName: string) => void;
  private onConnected?: (peerId: string) => void;

  constructor() {
    discovery.onSignal(this.handleSignaling.bind(this));
  }

  setCallbacks(
    onMsg: (msg: Message) => void, 
    onProgress: (messageId: string, peerId: string, progress: number) => void,
    onPairRequest?: (fromId: string, fromName: string) => void,
    onConnected?: (peerId: string) => void
  ) {
    this.onMessageReceived = onMsg;
    this.onProgressUpdate = onProgress;
    this.onPairRequestReceived = onPairRequest;
    this.onConnected = onConnected;
  }

  private async handleSignaling(data: any) {
    const { from, type, signalType, payload, accepted } = data;

    if (type === 'pair_request') {
      this.onPairRequestReceived?.(from, data.name);
      return;
    }

    if (type === 'pair_response') {
      if (accepted) this.negotiate(from);
      return;
    }

    if (type === 'signal') {
      try {
        const pc = this.getOrCreateConnection(from);
        if (signalType === 'OFFER') {
          await pc.setRemoteDescription(new RTCSessionDescription(payload));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          discovery.sendSignal(from, 'ANSWER', answer);
          await this.processQueuedCandidates(from, pc);
        } else if (signalType === 'ANSWER') {
          await pc.setRemoteDescription(new RTCSessionDescription(payload));
          await this.processQueuedCandidates(from, pc);
        } else if (signalType === 'ICE' && payload) {
          if (pc.remoteDescription) {
            await pc.addIceCandidate(new RTCIceCandidate(payload)).catch(() => {});
          } else {
            if (!this.queuedCandidates.has(from)) {
              this.queuedCandidates.set(from, []);
            }
            this.queuedCandidates.get(from)!.push(payload);
          }
        }
      } catch (err) {}
    }
  }

  private async processQueuedCandidates(peerId: string, pc: RTCPeerConnection) {
    const queue = this.queuedCandidates.get(peerId);
    if (queue) {
      this.queuedCandidates.delete(peerId);
      for (const candidate of queue) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {}
      }
    }
  }

  private getOrCreateConnection(peerId: string): RTCPeerConnection {
    let pc = this.connections.get(peerId);
    if (pc && pc.connectionState !== 'closed') return pc;

    pc = new RTCPeerConnection(RTC_CONFIG);

    pc.onicecandidate = (e) => {
      if (e.candidate) discovery.sendSignal(peerId, 'ICE', e.candidate);
    };

    pc.ondatachannel = (e) => this.setupDataChannel(peerId, e.channel);
    
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        this.cleanup(peerId);
      }
    };

    this.connections.set(peerId, pc);
    return pc;
  }

  private async negotiate(peerId: string) {
    const pc = this.getOrCreateConnection(peerId);
    // Fix #1: Use a fully reliable ordered channel (no maxRetransmits).
    // Setting maxRetransmits creates a "partially-reliable" channel — chunks
    // dropped after N retries are silently lost, corrupting the assembled file.
    // Head-of-line blocking is a latency tradeoff we accept for data integrity.
    const dc = pc.createDataChannel('filespread-v1', { ordered: true });
    this.setupDataChannel(peerId, dc);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    discovery.sendSignal(peerId, 'OFFER', offer);
  }

  private cleanup(peerId: string) {
    this.dataChannels.get(peerId)?.close();
    this.connections.get(peerId)?.close();
    this.dataChannels.delete(peerId);
    this.connections.delete(peerId);
    this.receiveBuffers.delete(peerId);
    this.queuedCandidates.delete(peerId);
    
    if (this.heartbeats.has(peerId)) {
      clearInterval(this.heartbeats.get(peerId));
      this.heartbeats.delete(peerId);
    }
  }

  // Waits up to 15 s for the DataChannel to open, then resolves.
  // This prevents silent file-send failures when the user acts before
  // the WebRTC handshake finishes.
  private waitForChannel(peerId: string, timeoutMs = 15000): Promise<RTCDataChannel | null> {
    return new Promise(resolve => {
      const dc = this.dataChannels.get(peerId);
      if (dc?.readyState === 'open') { resolve(dc); return; }

      const deadline = Date.now() + timeoutMs;
      const poll = setInterval(() => {
        const ch = this.dataChannels.get(peerId);
        if (ch?.readyState === 'open') { clearInterval(poll); resolve(ch); return; }
        if (Date.now() >= deadline) { clearInterval(poll); resolve(null); }
      }, 200);
    });
  }

  private setupDataChannel(peerId: string, channel: RTCDataChannel) {
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = BUFFER_THRESHOLD;
    
    channel.onopen = () => {
      this.dataChannels.set(peerId, channel);
      this.onConnected?.(peerId);
      const hb = setInterval(() => {
        if (channel.readyState === 'open') {
          channel.send(JSON.stringify({ type: 'heartbeat' }));
        }
      }, HEARTBEAT_INTERVAL);
      this.heartbeats.set(peerId, hb);
    };
    
    channel.onmessage = async (e) => {
      if (typeof e.data === 'string') {
        try {
          const msg = JSON.parse(e.data);
          
          // Ignore heartbeats
          if (msg.type === 'heartbeat') return;

          // Handle incoming text or link messages
          if (msg.type === 'text' || msg.type === 'link') {
            this.onMessageReceived?.(msg);
            return;
          }

          if (msg.type === 'file-meta') {
            // Memory Guard Logic: Prevent tab crash
            if (msg.fileSize > MAX_BUFFERED_SIZE) {
              channel.send(JSON.stringify({ type: 'error', message: 'File too large for browser RAM.' }));
              return;
            }
            this.receiveBuffers.set(peerId, { chunks: [], receivedSize: 0, meta: msg });
          } else if (msg.type === 'file-end') {
            const buffer = this.receiveBuffers.get(peerId);
            if (buffer) {
              const blob = new Blob(buffer.chunks, { type: buffer.meta.fileType });
              this.onMessageReceived?.({
                ...buffer.meta,
                type: 'file',
                content: buffer.meta.fileName,
                fileData: blob,
                progress: 100
              });
              this.receiveBuffers.delete(peerId);
              
              // Fix #3: Chrome blocks a.click() downloads triggered outside a user
              // gesture (e.g., from a DataChannel message handler). We must:
              //   1. Append the anchor to the DOM so Chrome treats it as a real download.
              //   2. Delay revokeObjectURL — calling it synchronously after .click()
              //      revokes the URL before Chrome's async download queue can use it.
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = buffer.meta.fileName;
              a.style.display = 'none';
              document.body.appendChild(a);
              a.click();
              setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }, 1000);
            }
          } else if (msg.type === 'error') {
            this.onMessageReceived?.({
              id: 'err-' + Date.now(),
              senderId: peerId,
              receiverId: discovery.getMe().id,
              timestamp: Date.now(),
              type: 'text',
              content: `⚠️ Error: ${msg.message}`
            });
          }
        } catch (err) {}
      } else {
        const buffer = this.receiveBuffers.get(peerId);
        if (buffer) {
          buffer.chunks.push(e.data);
          buffer.receivedSize += e.data.byteLength;
          this.onProgressUpdate?.(buffer.meta.id, peerId, Math.floor((buffer.receivedSize / buffer.meta.fileSize) * 100));
        }
      }
    };

    channel.onclose = () => this.cleanup(peerId);
  }

  async connectToPeer(peerId: string) {
    if (this.dataChannels.get(peerId)?.readyState === 'open') return;
    discovery.sendPairRequest(peerId);
  }

  isConnected(peerId: string): boolean {
    return this.dataChannels.get(peerId)?.readyState === 'open';
  }

  async sendMessage(peerId: string, message: Message) {
    const dc = await this.waitForChannel(peerId);
    if (dc) dc.send(JSON.stringify(message));
  }

  async sendFile(peerId: string, file: File, messageId: string) {
    const dc = await this.waitForChannel(peerId);
    if (!dc) return;

    dc.send(JSON.stringify({
      type: 'file-meta',
      id: messageId,
      senderId: discovery.getMe().id,
      receiverId: peerId,
      timestamp: Date.now(),
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    }));

    const reader = file.stream().getReader();
    let sent = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          dc.send(JSON.stringify({ type: 'file-end', messageId }));
          break;
        }

        let offset = 0;
        while (offset < value.length) {
          // Fix #2: Backpressure race condition fix.
          // The original code checked bufferedAmount, then set onbufferedamountlow.
          // If the buffer drained between the check and the assignment, the event
          // fired before the handler was attached — causing an infinite await hang.
          // Fix: re-check bufferedAmount inside a helper that only awaits if the
          // buffer is still full when the handler is registered.
          if (dc.bufferedAmount > BUFFER_THRESHOLD) {
            await new Promise<void>(resolve => {
              // Re-check immediately: buffer may have drained already.
              if (dc.bufferedAmount <= BUFFER_THRESHOLD) {
                resolve();
                return;
              }
              dc.onbufferedamountlow = () => {
                dc.onbufferedamountlow = null;
                resolve();
              };
            });
          }

          const length = Math.min(CHUNK_SIZE, value.length - offset);
          const chunk = value.slice(offset, offset + length);
          dc.send(chunk);
          
          offset += length;
          sent += length;
          this.onProgressUpdate?.(messageId, peerId, Math.floor((sent / file.size) * 100));
        }
      }
    } catch (err) {}
  }
}

export const webrtc = new WebRTCService();
